import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { ClassRow, errorText, gradeLabel, NATIONAL_LEVELS, rpc } from './api';
import { LevelPicker } from './bits';
import { SchoolCtx, SupportGate } from './SchoolPage';
import { useClasses } from './useClasses';

// Classes tab (spec B1, B2, B4; D1, D3, D4) — in support mode. Class labels
// are generated from the school's grade levels × section scheme; never typed.
export function ClassesTab(ctx: SchoolCtx) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const s = ctx.detail.school;
  const [data, reload] = useClasses(s.id);
  const [ticked, setTicked] = useState<string[]>([]);
  const [size, setSize] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lists, setLists] = useState(false);
  const [assignFor, setAssignFor] = useState<ClassRow | { grade: string; label: string } | null>(null);
  const [archiveFor, setArchiveFor] = useState<ClassRow | null>(null);
  const [removeA, setRemoveA] = useState<{ id: string; label: string } | null>(null);
  const [sizeFor, setSizeFor] = useState<ClassRow | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  if (!data) return <p role="status" className="text-muted">{t.loading}</p>;

  const sections = data.sections.filter((x) => !x.disabled);
  const levels = NATIONAL_LEVELS.filter((l) => data.levels.includes(l));
  const active = data.classes.filter((c) => c.status === 'active');
  const archived = data.classes.filter((c) => c.status === 'archived');
  const gradeOf = (gid: string | null) => data.grades.find((g) => g.id === gid);
  const exists = (level: string, sectionId: string) =>
    active.some((c) => gradeOf(c.grade_id)?.level === level && c.section_id === sectionId);
  const cell = (level: string, sectionId: string) => `${level}|${sectionId}`;
  const ungraded = active.filter((c) => !c.grade_id);

  async function create() {
    setBusy(true);
    setError(null);
    const cells = ticked.map((k) => {
      const [level, section_id] = k.split('|');
      return { level, section_id, expected_size: Number(size) };
    });
    const r = await rpc<{ created: number }>('platform_add_classes', { p_school_id: s.id, p_cells: cells });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    setTicked([]);
    announce(t['pa.classesCreated'](r.data.created));
    await reload();
    await ctx.reload();
  }

  async function simple(fn: string, args: Record<string, unknown>, msg: string, after?: () => void) {
    setBusy(true);
    setError(null);
    const r = await rpc<unknown>(fn, args);
    setBusy(false);
    if (r.error) {
      setError(errorText(t, r.error));
      return false;
    }
    announce(msg);
    after?.();
    await reload();
    await ctx.reload();
    return true;
  }

  return (
    <>
      <SupportGate ctx={ctx} what={t['pa.whatClasses']}>
        <section className="panel p5" aria-labelledby={`${id}-nc`}>
          <div className="panel-head wrap">
            <h2 className="panel-title" id={`${id}-nc`}>
              {t['pa.namingConvention']}
            </h2>
            <button type="button" className="link-btn" onClick={() => setLists(true)}>
              {t['pa.editLists']}
            </button>
          </div>
          <p className="form-hint">
            {t['pa.levelsNow'](levels.map((l) => t[`lvl.${l}`]).join(', ') || '–')} ·{' '}
            {t['pa.sectionsNow'](sections.map((x) => x.label).join(', ') || '–')}
          </p>
        </section>

        <section className="panel p5" aria-labelledby={`${id}-add`}>
          <h2 className="panel-title" id={`${id}-add`}>
            {t['pa.addClasses']}
          </h2>
          <p className="form-hint">{t['pa.addClassesHint']}</p>
          {levels.length === 0 || sections.length === 0 ? (
            <p className="notice">{t['pa.needLists']}</p>
          ) : (
            <>
              <div className="table-wrap grid-wrap" tabIndex={0} role="region" aria-labelledby={`${id}-add`}>
                <table className="table grid-table">
                  <caption className="visually-hidden">{t['pa.gridCaption']}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t['pa.grade']}</th>
                      {sections.map((x) => (
                        <th scope="col" key={x.id}>
                          {x.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {levels.map((l) => (
                      <tr key={l}>
                        <th scope="row">{t[`lvl.${l}`]}</th>
                        {sections.map((x) => {
                          const k = cell(l, x.id);
                          const has = exists(l, x.id);
                          const label = `${gradeLabel(l)}${x.label}`;
                          return (
                            <td key={x.id}>
                              <label className={`grid-cell${has ? ' exists' : ticked.includes(k) ? ' on' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={has || ticked.includes(k)}
                                  disabled={has}
                                  onChange={(e) => setTicked(e.target.checked ? [...ticked, k] : ticked.filter((y) => y !== k))}
                                  aria-label={has ? t['pa.classExistsLabel'](label) : t['pa.createClassLabel'](label)}
                                />
                                <span aria-hidden="true">{label}</span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-row">
                <div className="field-group">
                  <label className="field-label" htmlFor={`${id}-sz`}>
                    {t['pa.expectedSize']}
                  </label>
                  <input
                    id={`${id}-sz`}
                    className="field field-num"
                    type="number"
                    min={1}
                    max={200}
                    inputMode="numeric"
                    value={size}
                    required
                    aria-required="true"
                    onChange={(e) => setSize(e.target.value)}
                    aria-describedby={`${id}-szh`}
                  />
                  <p className="form-hint" id={`${id}-szh`}>
                    {size && Number(size) < 5 ? t['pa.smallClassWarn'] : t['pa.expectedSizeHint']}
                  </p>
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={create}
                  disabled={busy || ticked.length === 0 || !validSize(size)}
                >
                  {t['pa.createNClasses'](ticked.length)}
                </button>
              </div>
            </>
          )}
        </section>
      </SupportGate>

      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}

      <section className="panel p5" aria-labelledby={`${id}-list`}>
        <div className="panel-head wrap">
          <h2 className="panel-title" id={`${id}-list`}>
            {t['pa.classesThisYear'](data.year?.label ?? '–')}
          </h2>
        </div>
        {active.length === 0 ? (
          <p className="text-muted">{t['pa.noClassesSchool']}</p>
        ) : (
          <div className="grade-groups">
            {[...data.grades.map((g) => ({ g, rows: active.filter((c) => c.grade_id === g.id) })), { g: null, rows: ungraded }]
              .filter((x) => x.rows.length > 0 || x.g)
              .map(({ g, rows }) => (
                <div key={g?.id ?? 'none'} className="grade-group">
                  <div className="grade-head">
                    <h3>{g ? t['pa.gradeN'](g.label) : t['pa.noGrade']}</h3>
                    {g && (
                      <span className="pill-row">
                        {g.leads.map((l) => (
                          <span key={l.assignment_id} className="chip">
                            {t['pa.leadChip'](l.name)}
                            {ctx.inSupport && (
                              <button
                                type="button"
                                className="chip-x"
                                onClick={() => setRemoveA({ id: l.assignment_id, label: t['pa.leadChip'](l.name) })}
                                aria-label={t['pa.removeAssignment'](l.name, t['pa.gradeN'](g.label))}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {ctx.inSupport && (
                          <button type="button" className="link-btn" onClick={() => setAssignFor({ grade: g.id, label: t['pa.gradeN'](g.label) })}>
                            {t['pa.addGradeLead']}
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                  {rows.length === 0 ? (
                    <p className="text-muted">{t['pa.noClassesInGrade']}</p>
                  ) : (
                    <ul className="class-rows">
                      {rows.map((c) => (
                        <li key={c.id}>
                          <span className="class-label">{c.label}</span>
                          {c.expected_size ? (
                            <span className="text-muted class-size">{t['pa.sizeN'](c.expected_size)}</span>
                          ) : (
                            <span className="class-size">
                              <Pill kind="warn">{t['pa.sizeUnknown']}</Pill>
                            </span>
                          )}
                          <span className="pill-row class-teachers">
                            {c.teachers.length === 0 && <Pill kind="warn">{t['pa.noTeacher']}</Pill>}
                            {c.teachers.map((x) => (
                              <span key={x.assignment_id} className="chip">
                                <Link to={`/admin/schools/${s.id}/people/${x.user_id}`}>{x.name}</Link>
                                <span className="text-muted"> · {t[`pa.kind_${x.kind}`]}</span>
                                {ctx.inSupport && (
                                  <button
                                    type="button"
                                    className="chip-x"
                                    onClick={() => setRemoveA({ id: x.assignment_id, label: `${x.name} · ${c.label}` })}
                                    aria-label={t['pa.removeAssignment'](x.name, c.label)}
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))}
                          </span>
                          {ctx.inSupport && (
                            <span className="class-actions">
                              <button type="button" className="link-btn" onClick={() => setAssignFor(c)} aria-label={t['pa.assignTo'](c.label)}>
                                {t['pa.assignShort']}
                              </button>
                              <button type="button" className="link-btn" onClick={() => setSizeFor(c)} aria-label={t['pa.editSizeOf'](c.label)}>
                                {t['pa.size']}
                              </button>
                              <button type="button" className="link-btn" onClick={() => setArchiveFor(c)} aria-label={t['pa.archiveOf'](c.label)}>
                                {c.has_sessions ? t['pa.archive'] : t['pa.delete']}
                              </button>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
          </div>
        )}
        {archived.length > 0 && (
          <div className="archived">
            <button type="button" className="link-btn" aria-expanded={showArchived} onClick={() => setShowArchived(!showArchived)}>
              {t['pa.archivedN'](archived.length)}
            </button>
            {showArchived && (
              <ul className="school-list">
                {archived.map((c) => (
                  <li key={c.id}>
                    <span>{c.label}</span>
                    {ctx.inSupport && (
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => simple('platform_restore_class', { p_class_id: c.id }, t['pa.restored'])}
                        aria-label={t['pa.restoreOf'](c.label)}
                      >
                        {t['pa.restore']}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <ListsDialog open={lists} ctx={ctx} levels={data.levels} sections={data.sections} onClose={() => setLists(false)} onSaved={reload} />

      <AssignDialog
        target={assignFor}
        teachers={data.teachers}
        onClose={() => setAssignFor(null)}
        onAssign={async (user, kind) => {
          if (!assignFor) return false;
          const grade = 'grade' in assignFor;
          setBusy(true);
          setError(null);
          const r = await rpc<{ added: number; second_homeroom: string[] }>('platform_assign', {
            p_user_id: user,
            p_kind: grade ? 'grade_lead' : kind,
            p_class_ids: grade ? null : [assignFor.id],
            p_grade_id: grade ? assignFor.grade : null,
          });
          setBusy(false);
          if (r.error) {
            setError(errorText(t, r.error));
            return false;
          }
          announce(r.data.second_homeroom.length ? t['pa.secondHomeroom'](r.data.second_homeroom.join(', ')) : t['pa.saved']);
          setAssignFor(null);
          await reload();
          await ctx.reload();
          return true;
        }}
        busy={busy}
        error={error}
      />

      <Dialog
        open={!!archiveFor}
        title={archiveFor?.has_sessions ? t['pa.archiveTitle'](archiveFor?.label ?? '') : t['pa.deleteTitle'](archiveFor?.label ?? '')}
        intro={<p className="form-hint">{archiveFor?.has_sessions ? t['pa.archiveClassIntro'] : t['pa.deleteIntro']}</p>}
        submitLabel={archiveFor?.has_sessions ? t['pa.archive'] : t['pa.delete']}
        danger
        busy={busy}
        error={error}
        onClose={() => setArchiveFor(null)}
        onSubmit={() =>
          archiveFor &&
          simple(
            'platform_archive_class',
            { p_class_id: archiveFor.id, p_delete: !archiveFor.has_sessions },
            archiveFor.has_sessions ? t['pa.archivedDone'] : t['pa.deletedDone'],
            () => setArchiveFor(null),
          )
        }
      />

      <Dialog
        open={!!removeA}
        title={t['pa.unassignFrom'](removeA?.label ?? '')}
        intro={<p className="form-hint">{t['pa.unassignIntro']}</p>}
        submitLabel={t['pa.unassign']}
        danger
        busy={busy}
        error={error}
        onClose={() => setRemoveA(null)}
        onSubmit={() =>
          removeA && simple('platform_unassign', { p_assignment_id: removeA.id }, t['pa.saved'], () => setRemoveA(null))
        }
      />

      <SizeDialog
        target={sizeFor}
        busy={busy}
        error={error}
        onClose={() => setSizeFor(null)}
        onSave={(n) =>
          sizeFor && simple('platform_update_class', { p_class_id: sizeFor.id, p_expected_size: n }, t['pa.saved'], () => setSizeFor(null))
        }
      />
    </>
  );
}

function SizeDialog({
  target,
  busy,
  error,
  onClose,
  onSave,
}: {
  target: ClassRow | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (n: number | null) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const [v, setV] = useState('');
  return (
    <Dialog
      open={!!target}
      title={t['pa.editSizeOf'](target?.label ?? '')}
      submitLabel={t['pa.save']}
      busy={busy}
      error={error}
      onClose={onClose}
      submitDisabled={!validSize(v)}
      onSubmit={() => onSave(Number(v))}
    >
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-s`}>
          {t['pa.expectedSize']}
        </label>
        <input
          id={`${id}-s`}
          className="field field-num"
          type="number"
          min={1}
          max={200}
          value={v}
          required
          aria-required="true"
          placeholder={target?.expected_size ? String(target.expected_size) : undefined}
          onChange={(e) => setV(e.target.value)}
          aria-describedby={`${id}-h`}
        />
        <p className="form-hint" id={`${id}-h`}>
          {v && Number(v) < 5 ? t['pa.smallClassWarn'] : t['pa.expectedSizeHint']}
        </p>
      </div>
    </Dialog>
  );
}

function AssignDialog({
  target,
  teachers,
  onClose,
  onAssign,
  busy,
  error,
}: {
  target: ClassRow | { grade: string; label: string } | null;
  teachers: { user_id: string; name: string; is_test: boolean }[];
  onClose: () => void;
  onAssign: (user: string, kind: 'homeroom' | 'subject') => Promise<boolean>;
  busy: boolean;
  error: string | null;
}) {
  const { t } = useI18n();
  const id = useId();
  const [user, setUser] = useState('');
  const [kind, setKind] = useState<'homeroom' | 'subject'>('homeroom');
  const [q, setQ] = useState('');
  const grade = !!target && 'grade' in target;
  const label = target ? target.label : '';
  const hasHomeroom = !!target && !grade && (target as ClassRow).teachers.some((x) => x.kind === 'homeroom');
  const shown = teachers.filter((x) => x.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Dialog
      open={!!target}
      title={grade ? t['pa.addGradeLeadTo'](label) : t['pa.assignTo'](label)}
      submitLabel={t['pa.assign']}
      busy={busy}
      error={error}
      submitDisabled={!user}
      onClose={() => {
        setUser('');
        onClose();
      }}
      onSubmit={async () => {
        if (await onAssign(user, kind)) setUser('');
      }}
    >
      {teachers.length === 0 ? (
        <p className="notice">{t['pa.noTeachersYet']}</p>
      ) : (
        <>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-q`}>
              {t['pa.searchStaff']}
            </label>
            <input id={`${id}-q`} className="field" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <fieldset className="pick-list">
            <legend className="field-label">{t['pa.teacher']}</legend>
            {shown.map((x) => (
              <label key={x.user_id} className={`choice${user === x.user_id ? ' on' : ''}`}>
                <input type="radio" name={`${id}-u`} checked={user === x.user_id} onChange={() => setUser(x.user_id)} /> {x.name}{' '}
                {x.is_test && <span className="test-badge">{t['ctx.testBadge']}</span>}
              </label>
            ))}
          </fieldset>
          {!grade && (
            <fieldset className="inline-radios">
              <legend className="field-label">{t['pa.kind']}</legend>
              {(['homeroom', 'subject'] as const).map((k) => (
                <label key={k}>
                  <input type="radio" name={`${id}-k`} checked={kind === k} onChange={() => setKind(k)} /> {t[`pa.kind_${k}`]}
                </label>
              ))}
            </fieldset>
          )}
          {!grade && kind === 'homeroom' && hasHomeroom && <p className="notice">{t['pa.secondHomeroomWarn']}</p>}
        </>
      )}
    </Dialog>
  );
}

function ListsDialog({
  open,
  ctx,
  levels,
  sections,
  onClose,
  onSaved,
}: {
  open: boolean;
  ctx: SchoolCtx;
  levels: string[];
  sections: { id: string; label: string; disabled: boolean; in_use: boolean }[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const s = ctx.detail.school;
  const [lv, setLv] = useState<string[]>(levels);
  const [sec, setSec] = useState<string[]>(sections.filter((x) => !x.disabled).map((x) => x.label));
  const [scheme, setScheme] = useState<'letters' | 'numbers' | 'custom'>('custom');
  const [newSec, setNewSec] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function applyScheme(v: 'letters' | 'numbers' | 'custom') {
    setScheme(v);
    if (v === 'letters') setSec(['A', 'B', 'C', 'D', 'E', 'F']);
    if (v === 'numbers') setSec(['1', '2', '3', '4', '5', '6']);
  }

  async function save() {
    setBusy(true);
    setError(null);
    const a = await rpc('platform_update_school', {
      p_school_id: s.id,
      p_name: s.name,
      p_municipality: s.municipality_code,
      p_official_number: s.official_number,
      p_stages: lv,
      p_default_language: s.default_language,
      p_tier: s.tier,
    });
    if (a.error) {
      setBusy(false);
      return setError(errorText(t, a.error));
    }
    const b = await rpc('platform_set_sections', { p_school_id: s.id, p_labels: sec });
    setBusy(false);
    if (b.error) return setError(errorText(t, b.error));
    announce(t['pa.saved']);
    onClose();
    await onSaved();
    await ctx.reload();
  }

  return (
    <Dialog
      open={open}
      wide
      title={t['pa.namingConvention']}
      intro={<p className="form-hint">{t['pa.listsIntro']}</p>}
      submitLabel={t['pa.save']}
      busy={busy}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      <LevelPicker value={lv} onChange={setLv} legend={t['pa.stages']} hint={t['pa.levelsInUseHint']} />
      <fieldset className="inline-radios">
        <legend className="field-label">{t['pa.sectionScheme']}</legend>
        {(['letters', 'numbers', 'custom'] as const).map((v) => (
          <label key={v}>
            <input type="radio" name={`${id}-sch`} checked={scheme === v} onChange={() => applyScheme(v)} /> {t[`pa.scheme_${v}`]}
          </label>
        ))}
      </fieldset>
      <ul className="section-chips" aria-label={t['pa.sections']}>
        {sec.map((x) => {
          const used = sections.find((y) => y.label === x)?.in_use;
          return (
            <li key={x} className="chip">
              {x}
              <button type="button" className="chip-x" onClick={() => setSec(sec.filter((y) => y !== x))} aria-label={t['pa.removeSection'](x)}>
                ×
              </button>
              {used && <span className="visually-hidden">{t['pa.inUse']}</span>}
            </li>
          );
        })}
      </ul>
      <div className="form-row">
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-ns`}>
            {t['pa.addSection']}
          </label>
          <input id={`${id}-ns`} className="field field-num" maxLength={8} value={newSec} onChange={(e) => setNewSec(e.target.value)} />
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={!newSec.trim() || sec.includes(newSec.trim().toUpperCase())}
          onClick={() => {
            setSec([...sec, newSec.trim().toUpperCase()]);
            setNewSec('');
            setScheme('custom');
          }}
        >
          {t['pa.add']}
        </button>
      </div>
      <p className="form-hint">{t['pa.sectionsInUseHint']}</p>
    </Dialog>
  );
}

// Expected size is required (decision 11 October 2026): a whole number 1–200.
function validSize(v: string): boolean {
  const n = Number(v);
  return v.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= 200;
}
